import React, { useState, useMemo, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import {
  Fingerprint,
  Shield,
  Clock,
  Network,
  Server,
  Users,
  Check,
  ArrowRight,
  ArrowLeft,
  Sparkles,
  ChevronRight,
  Minus,
  Plus,
  Zap,
  Activity,
  RotateCcw,
  ShoppingCart,
} from 'lucide-react'
import SEOHead from '../components/SEOHead'
import { sendMetaEvent } from '../utils/metaCapi'

const BASE_PRICE = 15000
const WHATSAPP_NUMBER = '201010668822'
const USD_RATE = 50 // 1 USD ≈ 50 EGP

const capabilities = [
  'Real-time attendance feed from fingerprint device webhooks',
  'Violation tracking with staged escalation (mild / severe / tanbeeh)',
  'Automated deductions and overtime calculation',
  'Employee management with per-employee policy overrides',
  'Payroll master sheet generation (base, allowances, additions, deductions, net)',
  'Multi-branch support (optional add-on)',
  'Custom violation logic per company HR policy',
  'CSV import/export for employees and payroll',
  'Auto-archiving of attendance and payroll data',
  'Live dashboard with KPIs and real-time alerts',
  'Admin & HR roles with permission control',
]

const capabilitiesAr = [
  'تتبع الحضور المباشر من أجهزة البصمة',
  'تتبع المخالفات مع تصاعد مرحل (بسيط / شديد / تنبيه)',
  'حساب الخصومات والأوفرتايم تلقائياً',
  'إدارة الموظفين مع سياسات مخصصة لكل موظف',
  'إنشاء جدول رواتب رئيسي (الأساسي، البدلات، الإضافات، الخصومات، الصافي)',
  'دعم فروع متعددة (إضافة اختيارية)',
  'منطق مخالفات مخصص وفقاً لسياسة الموارد البشرية',
  'استيراد وتصدير CSV للموظفين وكشوف الرواتب',
  'أرشيف تلقائي لبيانات الحضور وكشوف الرواتب',
  'لوحة تحكم مباشرة مع مؤشرات KPI وتنبيهات فورية',
  'أدوار مدير وموارد بشرية مع التحكم بالصلاحيات',
]

const translations = {
  product: { en: 'Product', ar: 'المنتج' },
  heroTitle: { en: ' — Attendance, Payroll & Violations', ar: ' — الحضور والرواتب والمخالفات' },
  heroDesc: { en: 'An automated, policy-driven system that connects your fingerprint devices, processes punch logs in real-time, calculates deductions and overtime, and generates payroll master sheets — all from one dashboard.', ar: 'نظام آلي يعمل بالسياسات يتصل بأجهزة البصمة، يعالج سجلات الحضور في الوقت الحقيقي، يحسب الخصومات والأوفرتايم، ويولد جداول الرواتب — كل ذلك من لوحة تحكم واحدة.' },
  startingFrom: { en: 'Starting from', ar: 'يبدأ من' },
  standardDelivery: { en: 'standard delivery', ar: 'توصيل عادي' },
  firstDeviceFree: { en: 'First device', ar: 'الجهاز الأول' },
  free: { en: 'free', ar: 'مجاني' },
  startBuilding: { en: 'Start Building Your Package', ar: 'ابدأ في بناء باقتك' },
  whatsIncluded: { en: "See What's Included", ar: 'شاهد ما هو مشمول' },
  theProduct: { en: 'The Product', ar: 'المنتج' },
  whatPayrollXDoes: { en: 'What PayrollX does', ar: 'ماذا يفعل PayrollX' },
  builtFor: { en: 'Built for Egyptian companies with 10–500+ employees across production, logistics, retail, and admin.', ar: 'مصمم للشركات المصرية من 10 إلى 500+ موظف في الإنتاج والخدمات اللوجستية والتجزئة والإدارة.' },
  buildYourPackage: { en: 'Build Your Package', ar: 'ابنِ باقتك' },
  chooseWhat: { en: 'Choose what you need.', ar: 'اختر ما تحتاجه.' },
  seePriceLive: { en: 'See your price live.', ar: 'شاهد سعرك مباشرة.' },
  likeBuilding: { en: 'Like building a custom order — pick your components and send your configuration straight to WhatsApp.', ar: 'مثل بناء طلب مخصص — اختر مكوناتك وأرسل تكوينك مباشرة إلى واتساب.' },
  basePackage: { en: 'Base Package', ar: 'الباقة الأساسية' },
  basePackageDesc: { en: 'Dashboard, webhook integration, payroll master sheet, training & docs.', ar: 'لوحة تحكم، تكامل Webhook، جدول رواتب رئيسي، تدريب ووثائق.' },
  deliverySpeed: { en: 'Delivery speed', ar: 'سرعة التوصيل' },
  rush2wk: { en: 'Rush — 2 weeks', ar: 'عاجل — أسبوعين' },
  exp3wk: { en: 'Expedited — 3 weeks', ar: 'مسار سريع — 3 أسابيع' },
  std4wk: { en: 'Standard — 4 weeks', ar: 'عادي — 4 أسابيع' },
  fingerprintDevices: { en: 'Fingerprint Devices', ar: 'أجهزة البصمة' },
  numberOfDevices: { en: 'Number of devices', ar: 'عدد الأجهزة' },
  devicesDesc: { en: '1st free · 2nd–5th EGP 1,200 each · 6th+ EGP 1,000 each', ar: 'الأول مجاني · الثاني إلى الخامس بـ 1,200 جنيه · السادس فما فوق بـ 1,000 جنيه' },
  compatible: { en: 'Compatible: ZKTeco, Realtime, any webhook push device', ar: 'متوافق مع: ZKTeco، Realtime، أي جهاز يدعم Webhook' },
  violationPolicy: { en: 'Violation Policy', ar: 'سياسة المخالفات' },
  standardPolicy: { en: 'Standard policy', ar: 'السياسة العادية' },
  standardPolicyDesc: { en: 'Mild 18–30 min (editable), severe 31+ min (editable), grace, tanbeeh, escalation bands', ar: 'بسيط 18–30 دقيقة (قابل للتعديل)، شديد 31+ دقيقة (قابل للتعديل)، فترة سماح، تنبيه، نطاقات تصاعد' },
  customPolicy: { en: 'Custom policy', ar: 'سياسة مخصصة' },
  customPolicyDesc: { en: 'Custom rules tailored to your company HR policy', ar: 'قواعد مخصصة وفقاً لسياسة الموارد البشرية لشركتك' },
  multiBranch: { en: 'Multi-Branch', ar: 'فروع متعددة' },
  numberOfBranches: { en: 'Number of branches', ar: 'عدد الفروع' },
  branchesDesc: { en: '1st free · each additional branch EGP 1,000', ar: 'الأول مجاني · كل فرع إضافي بـ 1,000 جنيه' },
  perBranch: { en: 'Per-branch devices & reports', ar: 'أجهزة وتقارير لكل فرع' },
  employees: { en: 'Employees', ar: 'الموظفون' },
  numberOfEmployees: { en: 'Number of employees', ar: 'عدد الموظفين' },
  employeesDesc: { en: 'First 50 free · EGP 5/employee/month after', ar: 'أول 50 مجاني · 5 جنيه/موظف/شهر بعدها' },
  hosting: { en: 'Hosting & Domain', ar: 'الاستضافة والنطاق' },
  weHostMonthly: { en: 'We host — monthly', ar: 'نستضيف — شهري' },
  weHostYearly: { en: 'We host — yearly', ar: 'نستضيف — سنوي' },
  selfHost: { en: 'Self-hosted deployment', ar: 'نشر ذاتي' },
  selfHostDesc: { en: 'We deploy to your VPS. No recurring hosting cost.', ar: 'نشر على السيرفر الخاص بك. بدون تكاليف استضافة متكررة.' },
  domain: { en: 'Domain', ar: 'النطاق' },
  ownDomain: { en: "Client's own domain", ar: 'النطاق الخاص بالعميل' },
  throughUs: { en: 'Through us (.com / .net)', ar: 'من خلالنا (.com / .net)' },
  branding: { en: 'Branding', ar: 'العلامة التجارية' },
  keepBranding: { en: 'Keep Nodatx branding', ar: 'الحفاظ على علامة Nodatx' },
  keepBrandingDesc: { en: 'Dashboard shows "Powered by Nodatx"', ar: 'اللوحة تعرض "مدعوم من Nodatx"' },
  removeBranding: { en: 'Remove branding', ar: 'إزالة العلامة التجارية' },
  removeBrandingDesc: { en: 'White-label the dashboard with your company branding', ar: 'إعادة تسمية اللوحة بعلامة شركتك التجارية' },
  support: { en: 'Support', ar: 'الدعم' },
  supportDesc: { en: '30 days free, then per task', ar: '30 يوم مجاني، ثم حسب المهمة' },
  supportDetail: { en: 'After the first 30 days of free support, additional help is billed per task. No recurring fees.', ar: 'بعد أول 30 يوماً من الدعم المجاني، يتم فواتير المساعدة الإضافية حسب المهمة. بدون رسوم متكررة.' },
  includedModules: { en: 'Included Modules', ar: 'الوحدات المشمولة' },
  modulesDesc: { en: 'No OT · Special Rule · Special OT', ar: 'بدون أوفرتايم · قواعد خاصة · أوفرتايم خاص' },
  modulesDetail: { en: 'All three modules are included with every package at no extra cost. No OT disables overtime for specific employees. Special Rule adds per-employee policy overrides. Special OT adds tiered rates and weekend/holiday multipliers.', ar: 'الثلاثة وحدات مشمولة مع كل باقة بدون تكلفة إضافية. بدون أوفرتايم يلغى الأوفرتايم لموظفين معينين. القواعد الخاصة تضيف استثناءات لكل موظف. الأوفرتايم الخاص يضيف نسب تصاعدية ومضاعفات عطلة نهاية الأسبوع.' },
  yourPackage: { en: 'Your Package', ar: 'باقتك' },
  reset: { en: 'Reset', ar: 'إعادة تعيين' },
  oneTimeTotal: { en: 'One-time total', ar: 'المجموع لمرة واحدة' },
  recurring: { en: 'Recurring', ar: 'متكرر' },
  sendToWhatsApp: { en: 'Send to WhatsApp', ar: 'إرسال إلى واتساب' },
  whatsappDesc: { en: 'Your configuration details + estimated total will be pre-filled in a WhatsApp message to our team.', ar: 'تفاصيل تكوينك + المجموع المقدر ستكون جاهزة في رسالة واتساب لفريقنا.' },
  instantQuote: { en: 'Instant quote', ar: 'عرض سعر فوري' },
  noCommitment: { en: 'No commitment', ar: 'بدون التزام' },
  faq: { en: 'FAQ', ar: 'الأسئلة الشائعة' },
  faqTitle: { en: 'Frequently asked', ar: 'الأسئلة الشائعة' },
  required: { en: 'Required', ar: 'مطلوب' },
  step: { en: 'Step', ar: 'خطوة' },
  plus: { en: '+EGP', ar: '+جنيه' },
}

const formatEGP = (n) => `EGP ${n.toLocaleString('en-US')}`
const formatUSD = (n) => {
  const converted = n / USD_RATE
  if (converted < 1 && converted > 0) return `USD ${converted.toFixed(2)}`
  return `USD ${Math.round(converted).toLocaleString('en-US')}`
}

const OptionRow = ({ active, onClick, title, price, description }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left p-4 rounded-xl border transition-all duration-200 ${
      active
        ? 'border-purple-500 bg-purple-500/10 shadow-[0_0_30px_-15px_rgba(139,92,246,0.5)]'
        : 'border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/5'
    }`}
  >
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        <div
          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
            active ? 'border-purple-400 bg-purple-500' : 'border-white/30'
          }`}
        >
          {active && <div className="w-2 h-2 rounded-full bg-white" />}
        </div>
        <div className="min-w-0">
          <span className={`font-medium text-sm ${active ? 'text-white' : 'text-gray-300'}`}>{title}</span>
          {description && <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{description}</p>}
        </div>
      </div>
      <span className={`text-sm font-semibold whitespace-nowrap ${active ? 'text-purple-300' : 'text-gray-400'}`}>
        {price}
      </span>
    </div>
  </button>
)

const Section = ({ icon: Icon, title, step, children, required, lang = 'en' }) => (
  <div>
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-xl bg-purple-500/15 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-purple-400" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-purple-400 uppercase tracking-wider">
            {lang === 'ar' ? 'خطوة' : 'Step'} {step}
          </span>
          {required && (
            <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase bg-purple-500/15 text-purple-400">
              {lang === 'ar' ? 'مطلوب' : 'Required'}
            </span>
          )}
        </div>
        <h3 className="text-lg font-bold text-white">{title}</h3>
      </div>
    </div>
    {children}
  </div>
)

export default function Packages() {
  const [config, setConfig] = useState({
    devices: 1,
    policy: 'standard',
    branches: 1,
    employees: 50,
    setup: 'standard',
    hosting: 'we-host-monthly',
    domain: 'own',
    branding: false,
  })

  const configuratorRef = useRef(null)
  const { i18n } = useTranslation()
  const lang = i18n.language === 'ar' ? 'ar' : 'en'
  const isAr = lang === 'ar'
  const [showBar, setShowBar] = useState(true)
  const [faqVisible, setFaqVisible] = useState(false)
  const [currency, setCurrency] = useState('EGP')
  const isUSD = currency === 'USD'

  const t = (key) => translations[key]?.[lang] || translations[key]?.['en'] || key

  // Remove old lang state
  // const [lang, setLang] = useState('en')

  const formatPrice = (egpAmount) => {
    if (isUSD) return formatUSD(egpAmount)
    return formatEGP(egpAmount)
  }

  const formatPriceLabel = (egpAmount) => {
    if (isUSD) return `USD ${Math.round(egpAmount / USD_RATE).toLocaleString('en-US')}`
    return `EGP ${egpAmount.toLocaleString('en-US')}`
  }

  const convertPriceInText = (text) => {
    if (!isUSD) return text
    return text.replace(/EGP\s*([\d,]+)/g, (match, num) => {
      const egp = parseInt(num.replace(/,/g, ''))
      return `USD ${Math.round(egp / USD_RATE).toLocaleString('en-US')}`
    })
  }

  useEffect(() => {
    const onScroll = () => {
      const cfg = configuratorRef.current
      if (!cfg) return
      const rect = cfg.getBoundingClientRect()
      const reached = rect.top < window.innerHeight - 100
      setShowBar(reached)

      const faq = document.getElementById('faq')
      if (faq) {
        const faqRect = faq.getBoundingClientRect()
        setFaqVisible(faqRect.top < window.innerHeight - 80)
      }
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const update = (key, value) => setConfig((p) => ({ ...p, [key]: value }))
  const calc = useMemo(() => {
    const lines = []

    const basePrice = BASE_PRICE
    const setupSurcharge =
      config.setup === 'expedited' ? Math.round(basePrice * 0.15) : config.setup === 'rush' ? Math.round(basePrice * 0.2) : 0
    const baseWithSetup = basePrice + setupSurcharge
    lines.push({
      label: `Base package${config.setup !== 'standard' ? ` (${config.setup === 'expedited' ? '3-week expedited' : '2-week rush'})` : ' (4-week delivery)'}`,
      oneTime: baseWithSetup,
      recurring: 0,
      recurringLabel: '',
    })

    let deviceCost = 0
    if (config.devices > 1) {
      const extraAtStandard = Math.min(config.devices - 1, 4)
      const extraAtBulk = Math.max(config.devices - 5, 0)
      deviceCost = extraAtStandard * 1200 + extraAtBulk * 1000
    }
    lines.push({
      label: `Fingerprint devices (${config.devices}${config.devices === 1 ? ' — 1st free' : ' total'})`,
      oneTime: deviceCost,
      recurring: 0,
      recurringLabel: '',
    })

    const policyMap = { standard: 0, custom: 1000 }
    const policyLabels = { standard: 'Standard', custom: 'Custom' }
    lines.push({
      label: `Violation policy — ${policyLabels[config.policy]}`,
      oneTime: policyMap[config.policy],
      recurring: 0,
      recurringLabel: '',
    })

    lines.push({
      label: 'Support — 30 days free, then per task',
      oneTime: 0,
      recurring: 0,
      recurringLabel: '',
    })

    const branchCount = config.branches
    const branchCost = Math.max(0, (branchCount - 1) * 1000)
    lines.push({
      label: `Multi-branch — ${branchCount === 1 ? 'Single location' : `${branchCount} branches`}`,
      oneTime: branchCost,
      recurring: 0,
      recurringLabel: '',
    })

    const hostingMap = {
      'we-host-monthly': { oneTime: 0, recurring: 600, label: 'We host (monthly)', recLabel: '/mo' },
      'we-host-yearly': { oneTime: 0, recurring: 6000, label: 'We host (yearly)', recLabel: '/yr' },
      'self-host': { oneTime: 1000, recurring: 0, label: 'Self-hosted deployment', recLabel: '' },
    }
    const h = hostingMap[config.hosting]
    lines.push({
      label: `Hosting — ${h.label}`,
      oneTime: h.oneTime,
      recurring: h.recurring,
      recurringLabel: h.recLabel,
    })

    if (config.domain === 'through-us') {
      lines.push({
        label: 'Domain — through us',
        oneTime: 0,
        recurring: 500,
        recurringLabel: '/yr',
      })
    }

    if (config.branding) {
      lines.push({
        label: 'Remove Nodatx branding',
        oneTime: 500,
        recurring: 0,
        recurringLabel: '',
      })
    }

    // Employee billing — first 50 free, then EGP 5/employee/month
    const FREE_EMPLOYEES = 50
    const EMP_PRICE = 5
    const empCount = Math.max(config.employees, 1)
    const billableEmployees = Math.max(0, empCount - FREE_EMPLOYEES)
    const empCost = billableEmployees * EMP_PRICE
    if (billableEmployees > 0) {
      lines.push({
        label: `Employees — ${empCount} total (${FREE_EMPLOYEES} free + ${billableEmployees} × EGP ${EMP_PRICE})`,
        oneTime: 0,
        recurring: empCost,
        recurringLabel: '/mo',
      })
    }

    const oneTimeTotal = lines.reduce((s, l) => s + l.oneTime, 0)
    const recurringTotal = lines.reduce((s, l) => s + l.recurring, 0)

    return { lines, oneTimeTotal, recurringTotal }
  }, [config])

  const buildWhatsAppMessage = () => {
    const setupLabels = {
      standard: isAr ? `عادي 4 أسابيع (+${isUSD ? 'USD 0' : '0 جنيه'})` : (isUSD ? 'Standard 4 weeks (+USD 0)' : 'Standard 4 weeks (+EGP 0)'),
      expedited: isAr ? `مسار سريع 3 أسابيع (+${isUSD ? 'USD 45' : '2,250 جنيه'})` : (isUSD ? 'Expedited 3 weeks (+USD 45)' : 'Expedited 3 weeks (+EGP 2,250)'),
      rush: isAr ? `عاجل أسبوعين (+${isUSD ? 'USD 60' : '3,000 جنيه'})` : (isUSD ? 'Rush 2 weeks (+USD 60)' : 'Rush 2 weeks (+EGP 3,000)'),
    }
    const policyLabels = {
      standard: isAr ? 'عادي (مجاني)' : 'Standard (free)',
      custom: isAr ? `مخصص — ${isUSD ? 'USD 20' : '1,000 جنيه'}` : (isUSD ? 'Custom — USD 20' : 'Custom — EGP 1,000'),
    }
    const hostingLabels = {
      'we-host-monthly': isAr ? `نستضيف — ${isUSD ? 'USD 12/شهر' : '600 جنيه/شهر'}` : (isUSD ? 'We host — USD 12/month' : 'We host — EGP 600/month'),
      'we-host-yearly': isAr ? `نستضيف — ${isUSD ? 'USD 120/سنة' : '6,000 جنيه/سنة'}` : (isUSD ? 'We host — USD 120/year' : 'We host — EGP 6,000/year'),
      'self-host': isAr ? `نشر ذاتي — ${isUSD ? 'USD 20 لمرة واحدة' : '1,000 جنيه لمرة واحدة'}` : (isUSD ? 'Self-hosted — USD 20 one-time' : 'Self-hosted — EGP 1,000 one-time'),
    }

    const empUnit = isAr ? (isUSD ? ' USD/شهر' : ' جنيه/شهر') : (isUSD ? ' USD/mo' : ' EGP/mo')
    const empUnitPrice = isUSD ? '0.10' : '5'

    const msg = [
      isAr ? 'مرحباً Nodatx! أريد باقة PayrollX' : 'Hi Nodatx! I would like to get a PayrollX package.',
      '',
      isAr ? 'التكوين الخاص بي:' : 'MY CONFIGURATION:',
      isAr ? `• الأساس: برنامج PayrollX + لوحة التحكم — ${formatPrice(BASE_PRICE)}` : `• Base: PayrollX software + dashboard — ${formatPrice(BASE_PRICE)}`,
      isAr ? `• الإعداد: ${setupLabels[config.setup]}` : `• Setup: ${setupLabels[config.setup]}`,
      isAr ? `• الأجهزة: ${config.devices} (الأول مجاني)` : `• Devices: ${config.devices} (1st free)`,
      isAr ? `• سياسة المخالفات: ${policyLabels[config.policy]}` : `• Violation policy: ${policyLabels[config.policy]}`,
      isAr ? '• الدعم: 30 يوم مجاني، ثم حسب المهمة' : '• Support: 30 days free, then per task',
      isAr
        ? `• الفروع: ${config.branches === 1 ? 'موقع واحد (مجاني)' : `${config.branches} فروع — ${formatPrice((config.branches - 1) * 1000)} (${config.branches - 1} × ${isUSD ? 'USD 20' : '1,000 جنيه'})`}`
        : `• Multi-branch: ${config.branches === 1 ? 'Single location (free)' : `${config.branches} branches — ${formatPrice((config.branches - 1) * 1000)} (${config.branches - 1} × ${isUSD ? 'USD 20' : 'EGP 1,000'})`}`,
      isAr
        ? `• الموظفون: ${config.employees} (أول 50 مجاني${config.employees > 50 ? `, ${config.employees - 50} إضافي × ${empUnitPrice}${empUnit} = ${formatPrice((config.employees - 50) * 5)}/mo` : ' — بدون تكلفة إضافية'})`
        : `• Employees: ${config.employees} (first 50 free${config.employees > 50 ? `, ${config.employees - 50} extra × ${empUnitPrice}${empUnit} = ${formatPrice((config.employees - 50) * 5)}/mo` : ' — no extra cost'})`,
      isAr ? `• الاستضافة: ${hostingLabels[config.hosting]}` : `• Hosting: ${hostingLabels[config.hosting]}`,
      isAr
        ? `• النطاق: ${config.domain === 'through-us' ? `من خلالنا — ${formatPrice(500)}/سنة` : 'النطاق الخاص بالعميل (مجاني)'}`
        : `• Domain: ${config.domain === 'through-us' ? `Through us — ${formatPrice(500)}/yr` : "Client's own (free)"}`,
      isAr
        ? `• إزالة العلامة التجارية: ${config.branding ? `نعم — ${formatPrice(500)}` : 'لا'}`
        : `• Remove branding: ${config.branding ? `Yes — ${formatPrice(500)}` : 'No'}`,
      isAr ? '• وحدة بدون أوفرتايم: مشمولة' : '• No OT module: Included',
      isAr ? '• وحدة قواعد خاصة: مشمولة' : '• Special Rule module: Included',
      isAr ? '• وحدة أوفرتايم خاص: مشمولة' : '• Special OT module: Included',
      '',
      isAr ? 'المجموع المقدر:' : 'ESTIMATED TOTAL:',
      isAr ? `• لمرة واحدة: ${formatPrice(calc.oneTimeTotal)}` : `• One-time: ${formatPrice(calc.oneTimeTotal)}`,
      isAr ? `• متكرر: ${formatPrice(calc.recurringTotal)} (البنود الشهرية ×12 إذا سنوية)` : `• Recurring: ${formatPrice(calc.recurringTotal)} (monthly items ×12 if yearly)`,
      '',
      isAr ? 'أتطلع لمناقشة هذا. شكراً!' : 'Looking forward to discussing this. Thanks!',
    ].join('\n')

    return encodeURIComponent(msg)
  }

  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${buildWhatsAppMessage()}`

  const resetConfig = () =>
    setConfig({
      devices: 1,
      policy: 'standard',
      branches: 1,
      employees: 50,
      setup: 'standard',
      hosting: 'we-host-monthly',
      domain: 'own',
      branding: false,
    })

  return (
    <main className={`pt-20 ${isAr ? 'rtl' : ''}`}>
      <SEOHead
        title="PayrollX — Build Your Package | Nodatx"
        description="PayrollX is an attendance + payroll + violations automation system by Nodatx. Configure your own package and send it to us on WhatsApp."
        canonical="https://nodatx.com/packages/payrollx"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Product',
          name: 'PayrollX by Nodatx',
          description:
            'Attendance + payroll + violations automation system. Connects fingerprint devices, processes punch logs in real-time, calculates deductions/overtime, and generates payroll master sheets.',
          brand: { '@type': 'Brand', name: 'Nodatx' },
          offers: { '@type': 'Offer', priceCurrency: 'EGP', price: '15000', availability: 'https://schema.org/InStock' },
        }}
      />

      {/* Product Hero */}
      <section className="relative py-16 sm:py-24 px-4 sm:px-6 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-[#0a0a0f]" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-purple-600/20 rounded-full blur-[150px]" />
          <div className="absolute top-1/3 right-1/4 w-[300px] h-[300px] bg-purple-500/10 rounded-full blur-[80px]" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff03_1px,transparent_1px),linear-gradient(to_bottom,#ffffff03_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_30%,#000_70%,transparent_100%)]" />
        </div>

        <div className="container-custom relative z-10">
          {/* Top-left back links */}
          <div className="flex items-center gap-2 sm:gap-3 mb-8">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-xs font-medium text-gray-300 hover:text-purple-400 hover:border-purple-500/50 transition-all duration-200 active:scale-95"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Nodatx
            </Link>
            <Link
              to="/packages"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-xs font-medium text-gray-300 hover:text-purple-400 hover:border-purple-500/50 transition-all duration-200 active:scale-95"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Packages
            </Link>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left: Text */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 border border-purple-500/30 rounded-full text-purple-400 text-xs font-semibold uppercase tracking-wider mb-6">
                <Fingerprint className="w-3.5 h-3.5" />
                {t('product')} · PayrollX
              </span>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.1] text-white">
                <span className="text-purple-400">PayrollX</span>
                <span className="block sm:inline">{t('heroTitle')}</span>
              </h1>

              <p className="mt-6 text-base sm:text-lg text-gray-400 max-w-xl leading-relaxed">
                {t('heroDesc')}
              </p>

              {/* Currency Toggle */}
              <div className="mt-6 flex items-center gap-2">
                <div className="flex items-center bg-white/5 border border-white/10 rounded-lg p-0.5">
                  <button
                    onClick={() => setCurrency('EGP')}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${currency === 'EGP' ? 'bg-purple-500 text-white' : 'text-gray-400 hover:text-white'}`}
                  >
                    EGP
                  </button>
                  <button
                    onClick={() => setCurrency('USD')}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${currency === 'USD' ? 'bg-purple-500 text-white' : 'text-gray-400 hover:text-white'}`}
                  >
                    USD
                  </button>
                </div>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3 text-xs">
                <span className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-gray-400">
                  {t('startingFrom')} <span className="text-white font-semibold">{formatPrice(BASE_PRICE)}</span>
                </span>
                <span className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-gray-400">
                  <span className="text-white font-semibold">4-week</span> {t('standardDelivery')}
                </span>
                <span className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-full text-gray-400">
                  {t('firstDeviceFree')} <span className="text-white font-semibold">{t('free')}</span>
                </span>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href="#configure"
                  className="group inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-purple-600 text-white font-semibold rounded-xl transition-all duration-200 hover:scale-105 active:scale-[0.98] hover:shadow-[0_0_40px_-10px_rgba(139,92,246,0.5)]"
                >
                  {t('startBuilding')}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
                <a
                  href="#overview"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-white/5 border border-white/20 text-white font-medium rounded-xl hover:bg-white/10 hover:border-purple-500/50 active:scale-[0.98] transition-all duration-200 backdrop-blur-md"
                >
                  {t('whatsIncluded')}
                </a>
              </div>
            </motion.div>

            {/* Right: Product image placeholder */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="order-1 lg:order-2 relative"
            >
              <div className="relative rounded-3xl overflow-hidden border border-purple-500/20 shadow-[0_0_120px_-40px_rgba(139,92,246,0.4)] aspect-[4/3] bg-gradient-to-br from-[#0f0f1a] to-[#0a0a0f]">
                <img
                  src="https://i.ibb.co/zVgngVNT/Nodatx-1920-x-1080-px-1.png"
                  alt="PayrollX by Nodatx"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What's included */}
      <section id="overview" className="py-16 px-4 sm:px-6 bg-[#0a0a0f] relative scroll-mt-24">
        <div className="container-custom relative z-10 max-w-5xl">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="text-purple-400 font-semibold text-sm uppercase tracking-wider">{t('theProduct')}</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 mb-4 text-white">{t('whatPayrollXDoes')}</h2>
            <p className="text-gray-400 text-base sm:text-lg">
              {t('builtFor')}
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {(isAr ? capabilitiesAr : capabilities).map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06, duration: 0.4 }}
                className="flex items-start gap-3 p-4 rounded-xl bg-white/5 border border-white/5"
              >
                <div className="w-6 h-6 rounded-md bg-purple-500/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3.5 h-3.5 text-purple-400" />
                </div>
                <span className="text-gray-300 text-sm leading-relaxed">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Configurator */}
      <section ref={configuratorRef} id="configure" className="py-16 sm:py-20 px-4 sm:px-6 bg-gradient-to-b from-[#0a0a0f] to-[#0d0d18] relative">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="text-purple-400 font-semibold text-sm uppercase tracking-wider">{t('buildYourPackage')}</span>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mt-3 mb-4 text-white">
              {t('chooseWhat')}
              <br />
              <span className="text-gray-400">{t('seePriceLive')}</span>
            </h2>
            <p className="text-gray-400 text-base sm:text-lg">
              {t('likeBuilding')}
            </p>
          </div>

          <div className="grid lg:grid-cols-[1fr_380px] gap-6 lg:gap-8 items-start max-w-6xl mx-auto w-full">
            {/* Left: options */}
            <div className="space-y-10">
              {/* Base + setup */}
              <Section icon={Sparkles} title={t('basePackage')} step={1} required lang={lang}>
                <div className="p-4 rounded-xl border border-purple-500/30 bg-purple-500/[0.07]">
                  <div className="flex items-center justify-between gap-3 mb-1">
                    <div>
                      <div className="font-medium text-white text-sm">PayrollX {lang === 'ar' ? 'الباقة الأساسية' : 'base package'}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{t('basePackageDesc')}</div>
                    </div>
                    <span className="text-purple-300 font-semibold text-sm whitespace-nowrap">{formatPrice(BASE_PRICE)}</span>
                  </div>
                </div>
                <div className="mt-3 space-y-2">
                  <div className="text-xs text-gray-500 uppercase tracking-wider px-1">{t('deliverySpeed')}</div>
                  {[
                    { key: 'rush', title: t('rush2wk'), price: isAr ? '+3,000 جنيه' : (isUSD ? '+USD 60' : '+EGP 3,000') },
                    { key: 'expedited', title: t('exp3wk'), price: isAr ? '+2,250 جنيه' : (isUSD ? '+USD 45' : '+EGP 2,250') },
                    { key: 'standard', title: t('std4wk'), price: t('free') },
                  ].map((o) => (
                    <OptionRow
                      key={o.key}
                      active={config.setup === o.key}
                      onClick={() => update('setup', o.key)}
                      title={o.title}
                      price={o.price}
                    />
                  ))}
                </div>
              </Section>

              {/* Devices */}
              <Section icon={Fingerprint} title={t('fingerprintDevices')} step={2} required lang={lang}>
                <div className="p-4 rounded-xl border border-white/10 bg-white/[0.03]">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="font-medium text-white text-sm">{t('numberOfDevices')}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{convertPriceInText(t('devicesDesc'))}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => update('devices', Math.max(1, config.devices - 1))}
                        className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 text-white flex items-center justify-center hover:bg-white/10 active:scale-90 transition-all"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="text-xl font-bold text-white w-8 text-center tabular-nums">{config.devices}</span>
                      <button
                        type="button"
                        onClick={() => update('devices', Math.min(20, config.devices + 1))}
                        className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 text-white flex items-center justify-center hover:bg-white/10 active:scale-90 transition-all"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-xs">
                    <span className="text-gray-500">{t('compatible')}</span>
                    <span className="text-purple-300 font-semibold">
                      {config.devices === 1 ? (isAr ? 'الأول مجاني' : '1st free') : formatPrice(calc.lines[1]?.oneTime || 0)}
                    </span>
                  </div>
                </div>
              </Section>

              {/* Policy */}
              <Section icon={Shield} title={t('violationPolicy')} step={3} required lang={lang}>
                <div className="space-y-2">
                  {[
                    { key: 'standard', title: t('standardPolicy'), price: t('free'), description: convertPriceInText(t('standardPolicyDesc')) },
                    { key: 'custom', title: t('customPolicy'), price: formatPrice(1000), description: convertPriceInText(t('customPolicyDesc')) },
                  ].map((o) => (
                    <OptionRow
                      key={o.key}
                      active={config.policy === o.key}
                      onClick={() => update('policy', o.key)}
                      title={o.title}
                      price={o.price}
                      description={o.description}
                    />
                  ))}
                </div>
              </Section>

              {/* Multi-branch */}
              <Section icon={Network} title={t('multiBranch')} step={4} lang={lang}>
                <div className="p-4 rounded-xl border border-white/10 bg-white/[0.03]">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="font-medium text-white text-sm">{t('numberOfBranches')}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{convertPriceInText(t('branchesDesc'))}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => update('branches', Math.max(1, config.branches - 1))}
                        className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 text-white flex items-center justify-center hover:bg-white/10 active:scale-90 transition-all"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="text-xl font-bold text-white w-8 text-center tabular-nums">{config.branches}</span>
                      <button
                        type="button"
                        onClick={() => update('branches', Math.min(20, config.branches + 1))}
                        className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 text-white flex items-center justify-center hover:bg-white/10 active:scale-90 transition-all"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-xs">
                    <span className="text-gray-500">{t('perBranch')}</span>
                    <span className="text-purple-300 font-semibold">
                      {config.branches === 1 ? (isAr ? 'فرع واحد (مجاني)' : 'Single (free)') : formatPrice((config.branches - 1) * 1000)}
                    </span>
                  </div>
                </div>
              </Section>

              {/* Employees */}
              <Section icon={Users} title={t('employees')} step={5} required lang={lang}>
                <div className="p-4 rounded-xl border border-white/10 bg-white/[0.03]">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="font-medium text-white text-sm">{t('numberOfEmployees')}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{convertPriceInText(t('employeesDesc'))}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => update('employees', Math.max(1, config.employees - 10))}
                        className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 text-white flex items-center justify-center hover:bg-white/10 active:scale-90 transition-all"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <input
                        type="number"
                        min={1}
                        value={config.employees}
                        onChange={(e) => update('employees', Math.max(1, parseInt(e.target.value) || 1))}
                        className="w-20 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-center font-bold text-lg focus:border-purple-500 focus:outline-none tabular-nums"
                      />
                      <button
                        type="button"
                        onClick={() => update('employees', config.employees + 10)}
                        className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 text-white flex items-center justify-center hover:bg-white/10 active:scale-90 transition-all"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between text-xs">
                    <span className="text-gray-500">
                      {config.employees <= 50
                        ? (isAr ? `${50 - config.employees} أماكن مجانية متبقية` : `${50 - config.employees} free slots remaining`)
                        : (isAr ? `${config.employees - 50} موظف × 5 جنيه/شهر` : (isUSD ? `${config.employees - 50} billable employees × USD ${Math.round(5/USD_RATE)}/mo` : `${config.employees - 50} billable employees × EGP 5/mo`))}
                    </span>
                    <span className="font-semibold text-purple-300">
                      {config.employees <= 50
                        ? t('free')
                        : (isAr ? `${(config.employees - 50) * 5} جنيه/شهر` : (isUSD ? `USD ${Math.round((config.employees - 50) * 5 / USD_RATE)}/mo` : `EGP ${(config.employees - 50) * 5}/mo`))}
                    </span>
                  </div>
                </div>
              </Section>

              {/* Hosting */}
              <Section icon={Server} title={t('hosting')} step={6} required lang={lang}>
                <div className="space-y-2">
                  {[
                    { key: 'we-host-monthly', title: t('weHostMonthly'), price: isAr ? '600 جنيه/شهر' : (isUSD ? 'USD 12/mo' : 'EGP 600/mo'), description: 'n8n, payroll system, backups, SSL, 99% SLA' },
                    { key: 'we-host-yearly', title: t('weHostYearly'), price: isAr ? '6,000 جنيه/سنة' : (isUSD ? 'USD 120/yr' : 'EGP 6,000/yr'), description: isAr ? 'نفس الشهري، الفوترة سنوية' : 'Same as monthly, billed yearly' },
                    { key: 'self-host', title: t('selfHost'), price: formatPrice(1000), description: t('selfHostDesc') },
                  ].map((o) => (
                    <OptionRow
                      key={o.key}
                      active={config.hosting === o.key}
                      onClick={() => update('hosting', o.key)}
                      title={o.title}
                      price={o.price}
                      description={o.description}
                    />
                  ))}
                </div>
                <div className="mt-3 space-y-2">
                  <div className="text-xs text-gray-500 uppercase tracking-wider px-1">{t('domain')}</div>
                  {[
                    { key: 'own', title: t('ownDomain'), price: t('free') },
                    { key: 'through-us', title: t('throughUs'), price: isAr ? '500 جنيه/سنة' : (isUSD ? 'USD 10/year' : 'EGP 500/year') },
                  ].map((o) => (
                    <OptionRow
                      key={o.key}
                      active={config.domain === o.key}
                      onClick={() => update('domain', o.key)}
                      title={o.title}
                      price={o.price}
                    />
                  ))}
                </div>
              </Section>

              {/* Remove branding */}
              <Section icon={Shield} title={t('branding')} step={7} lang={lang}>
                <div className="space-y-2">
                  {[
                    { key: false, title: t('keepBranding'), price: t('free'), description: t('keepBrandingDesc') },
                    { key: true, title: t('removeBranding'), price: formatPrice(500), description: t('removeBrandingDesc') },
                  ].map((o) => (
                    <OptionRow
                      key={String(o.key)}
                      active={config.branding === o.key}
                      onClick={() => update('branding', o.key)}
                      title={o.title}
                      price={o.price}
                      description={o.description}
                    />
                  ))}
                </div>
              </Section>

              {/* Support info */}
              <div className="p-4 rounded-xl border border-white/10 bg-white/[0.03] flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-purple-500/15 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-purple-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-purple-400 uppercase tracking-wider">{t('support')}</span>
                    <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase bg-green-500/15 text-green-400">{isAr ? 'مشمول' : 'Included'}</span>
                  </div>
                  <div className="font-medium text-white text-sm mt-0.5">{t('supportDesc')}</div>
                  <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                    {t('supportDetail')}
                  </p>
                </div>
              </div>

              {/* Included modules info */}
              <div className="p-4 rounded-xl border border-green-500/20 bg-green-500/[0.05] flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-green-500/15 flex items-center justify-center flex-shrink-0">
                  <Check className="w-5 h-5 text-green-400" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-green-400 uppercase tracking-wider">{t('includedModules')}</span>
                    <span className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase bg-green-500/15 text-green-400">{t('free')}</span>
                  </div>
                  <div className="font-medium text-white text-sm mt-0.5">{t('modulesDesc')}</div>
                  <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                    {t('modulesDetail')}
                  </p>
                </div>
              </div>
            </div>

            {/* Right: live summary */}
            <div className="lg:sticky lg:top-24">
              <div className="bg-gradient-to-br from-[#1a1a2e] to-[#0a0a0f] border border-purple-500/30 rounded-2xl p-6 shadow-[0_0_60px_-30px_rgba(139,92,246,0.4)]">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-bold flex items-center gap-2">
                    <ShoppingCart className="w-4 h-4 text-purple-400" />
                    {t('yourPackage')}
                  </h3>
                  <button
                    onClick={resetConfig}
                    className="text-xs text-gray-400 hover:text-purple-400 flex items-center gap-1 transition-colors"
                  >
                    <RotateCcw className="w-3 h-3" />
                    {t('reset')}
                  </button>
                </div>

                <div className="space-y-2 mb-4 max-h-[300px] overflow-y-auto pr-1">
                  {calc.lines.map((line, i) => (
                    <div key={i} className="flex items-start justify-between gap-3 text-xs py-1.5 border-b border-white/5 last:border-0">
                      <span className="text-gray-400 leading-snug">{line.label}</span>
                      <span className="text-gray-200 font-medium whitespace-nowrap text-right">
                        {line.oneTime > 0 && <span className="block">{formatPrice(line.oneTime)}</span>}
                        {line.recurring > 0 && (
                          <span className="block text-purple-300">
                            {formatPrice(line.recurring)}{line.recurringLabel}
                          </span>
                        )}
                        {line.oneTime === 0 && line.recurring === 0 && <span className="text-gray-500">{t('free')}</span>}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="space-y-2 pt-4 border-t border-purple-500/20">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">{t('oneTimeTotal')}</span>
                    <span className="text-2xl font-bold text-white">{formatPrice(calc.oneTimeTotal)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-400">{t('recurring')}</span>
                    <span className="text-base font-semibold text-purple-300">{formatPrice(calc.recurringTotal)}</span>
                  </div>
                </div>

                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => sendMetaEvent('Lead', 'payrollx_whatsapp_checkout')}
                  className="group mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-4 bg-[#25D366] text-white font-semibold rounded-xl transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] hover:shadow-[0_0_40px_-10px_rgba(37,211,102,0.5)]"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
                  </svg>
                  {t('sendToWhatsApp')}
                </a>
                <p className="text-xs text-gray-500 text-center mt-2 leading-relaxed">
                  {t('whatsappDesc')}
                </p>

                <div className="mt-4 pt-4 border-t border-white/5 flex items-center gap-3 text-xs text-gray-500">
                  <div className="flex items-center gap-1.5">
                    <Zap className="w-3.5 h-3.5 text-purple-400" /> {t('instantQuote')}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Activity className="w-3.5 h-3.5 text-purple-400" /> {t('noCommitment')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 sm:py-20 px-4 sm:px-6 bg-[#0a0a0f] relative">
        <div className="container-custom relative z-10 max-w-3xl">
          <div className="text-center mb-10">
            <span className="text-purple-400 font-semibold text-sm uppercase tracking-wider">{t('faq')}</span>
            <h2 className="text-3xl sm:text-4xl font-bold mt-3 mb-4 text-white">{t('faqTitle')}</h2>
          </div>
          <div className="space-y-3">
            {(isAr ? [
              { q: 'كيف يتم حساب سعر؟', a: 'تختار كل مكون — الأساس، الأجهزة، السياسة، الدعم، الفروع المتعددة، وقت الإعداد، الاستضافة، والإضافات. مجموعك هو مجموع اختياراتك.' },
              { q: 'هل يمكنني إضافة مكونات لاحقاً؟', a: 'نعم. الإضافات، الأجهزة الإضافية، الفروع المتعددة، والدعم الممتد يمكن إضافتها في أي وقت. قواعد المخالفات المخصصة يمكن إضافتها بعد الإطلاق.' },
              { q: 'ماذا يحدث بعد انتهاء فترة الدعم؟', a: 'شراء دعم ممتد بـ 1,500 جنيه/شهر، أو الاستمرار في استخدام النظام بشكل مستقل — النظام ملكك.' },
              { q: 'هل أنا أملك البرنامج؟', a: 'أنت تملك بياناتك وتكوينك. PayrollX مرخص لاستخدامك. العملاء المضيفون ذاتياً لديهم تحكم كامل في نشرهم.' },
              { q: 'هل يمكنني تغيير الاستضافة لاحقاً؟', a: 'نعم. رسوم الترحيل 1,500 جنيه مع صفر توقف — نتولى الترحيل الكامل.' },
              { q: 'كيف يتم حساب أسعار الإضافات إذا تغير عدد الموظفين؟', a: 'الفوترة تعدل تلقائياً في الأول من كل شهر بناءً على الموظفين النشطين. الموظفون المؤرشفون مستبعدون.' },
            ] : [
              { q: 'How is my price calculated?', a: 'You pick each component — base, devices, policy, support, multi-branch, setup time, hosting, and add-ons. Your total is the sum of your choices.' },
              { q: 'Can I add components later?', a: 'Yes. Add-ons, extra devices, multi-branch, and extended support can be added at any time. Custom violation rules can be added post-launch.' },
              { q: 'What happens after my support period ends?', a: 'Purchase extended support at EGP 1,500/month, or continue using the system independently — it is yours.' },
              { q: 'Do I own the software?', a: 'You own your data and configuration. PayrollX is licensed for your use. Self-hosted clients have full control of their deployment.' },
              { q: 'Can I switch hosting later?', a: 'Yes. Migration fee is EGP 1,500 with zero downtime — we handle the full migration.' },
              { q: 'How are add-on prices calculated if employee count changes?', a: 'Billing adjusts automatically on the 1st of each month based on active employees. Archived employees are excluded.' },
            ]).map((f, i) => (
              <details key={i} className="group bg-[#15151f] border border-white/10 rounded-2xl overflow-hidden hover:border-purple-500/30 transition-colors">
                <summary className="flex items-center justify-between gap-4 p-5 cursor-pointer list-none">
                  <span className="text-white font-medium text-sm sm:text-base">{f.q}</span>
                  <ChevronRight className="w-4 h-4 text-purple-400 flex-shrink-0 transition-transform group-open:rotate-90" />
                </summary>
                <div className="px-5 pb-5 -mt-1 text-gray-400 text-sm leading-relaxed">{convertPriceInText(f.a)}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Sticky bottom bar */}
      {showBar && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          className={`fixed bottom-0 left-0 right-0 z-40 transition-opacity duration-300 ${
            faqVisible ? 'opacity-0 pointer-events-none translate-y-4' : 'opacity-100'
          }`}
        >
          <div className="bg-[#0a0a0f]/95 backdrop-blur-xl border-t border-purple-500/30 shadow-[0_-10px_50px_-20px_rgba(139,92,246,0.5)]">
            <div className="container-custom py-3 px-4">
              <div className="flex items-center justify-between gap-4">
                {/* Totals */}
                <div className="flex items-center gap-4 sm:gap-6 min-w-0">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500">{t('oneTimeTotal')}</div>
                    <div className="text-base sm:text-lg font-bold text-white leading-tight truncate">
                      {formatPrice(calc.oneTimeTotal)}
                    </div>
                  </div>
                  <div className="hidden sm:block h-8 w-px bg-white/10" />
                  <div className="hidden sm:block min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-gray-500">{t('recurring')}</div>
                    <div className="text-base font-semibold text-purple-300 leading-tight truncate">
                      {formatPrice(calc.recurringTotal)}
                    </div>
                  </div>
                </div>

                {/* WhatsApp button */}
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => sendMetaEvent('Lead', 'payrollx_whatsapp_checkout_bar')}
                  className="group flex-shrink-0 inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-[#25D366] text-white font-semibold rounded-xl transition-all duration-200 hover:scale-105 active:scale-[0.98] hover:shadow-[0_0_30px_-10px_rgba(37,211,102,0.5)] text-sm"
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 0 0-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
                  </svg>
                  <span className="hidden xs:inline sm:inline">{t('sendToWhatsApp')}</span>
                  <span className="xs:hidden sm:hidden">{isAr ? 'إرسال' : 'Send'}</span>
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </main>
  )
}
